import { useAttachHunterAffiliateCodeMutation } from "Api/Mutations/Roles/useAttachHunterAffiliateCodeMutation";
import {
  type AffiliateCodeError,
  getAffiliateCodeError,
} from "Components/Affiliate/AffiliateCodeField/affiliateCodeErrors";
import { useAffiliateReferrer } from "Components/Affiliate/AffiliateCodeField/useAffiliateReferrer";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useState } from "react";

/**
 * The hunter wizard's license-step affiliate code (ZS-1024). Unlike the Customer and Merchant
 * fields, "Ověřit" attaches the code to the Onboarding Hunter role right away, so the license
 * offer switches to the affiliate price; an unpaid order priced without it is re-priced by the
 * backend and keeps its payment details. Disabled (no field) without the Onboarding Hunter role.
 */
export const useHunterAffiliateCode = (identityUserPublicID?: string) => {
  const isEnabled = !!identityUserPublicID;
  const { lockedReferrer, isLoading } = useAffiliateReferrer(
    AccessRoleCodes.Hunter,
    { isEnabled },
  );
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<AffiliateCodeError | null>(null);
  const {
    mutate: attachCode,
    data: attachedAffiliate,
    isPending: isAttaching,
  } = useAttachHunterAffiliateCodeMutation({
    onSuccess: () => setError(null),
    onError: (attachError) => setError(getAffiliateCodeError(attachError)),
  });

  // A changed code is no longer the one rejected, so its error goes with it.
  const changeInput = (value: string) => {
    setInputValue(value);
    setError(null);
  };

  const attach = () => {
    const code = inputValue.trim();
    if (!code || !identityUserPublicID) {
      return;
    }

    attachCode({ identityUserPublicID, code });
  };

  return {
    isEnabled,
    fieldProps: {
      lockedReferrer,
      isLoading,
      inputValue,
      onInputChange: changeInput,
      onVerify: attach,
      verification: attachedAffiliate ?? null,
      error,
      isVerifying: isAttaching,
    },
    /** What the offered prices depend on beyond region and license. */
    pricingKey: lockedReferrer?.code ?? attachedAffiliate?.code,
  };
};
